import { useCallback, useEffect, useState } from "react";
import { SetFieldIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppDispatch } from "../../../Redux/redux-store";
import CommonValidatorProps from "./commonProps";

type validator<T> = (value?: T) => [boolean, string];

export interface UseValidatorProps<T> extends CommonValidatorProps<T> {
  validators: validator<T>[];
}

const useValidator = <T,>(props: UseValidatorProps<T>) => {
  const { formId, formSectionId, fieldId, initialState, initialValue, validators } = props;
  const dispatch = useAppDispatch();

  const [isValid, setIsValid] = useState(initialState !== undefined ? initialState : false);
  const [errorMessage, setErrorMessage] = useState("");

  const validate = useCallback(
    (value?: T) => {
      for (let i = 0; i < validators.length; i++) {
        const _validator = validators[i];
        const [_isValid, _errorMessage] = _validator(value);
        if (!_isValid) {
          if (_isValid !== isValid || _errorMessage !== errorMessage) {
            setIsValid(false);
            setErrorMessage(_errorMessage);
            dispatch(
              SetFieldIsValid({ formId, formSectionId, fieldId, fieldIsValid: false, fieldErrorMessage: _errorMessage })
            );
          }
          return;
        }
      }

      setIsValid(true);
      setErrorMessage("");
      dispatch(SetFieldIsValid({ formId, formSectionId, fieldId, fieldIsValid: true, fieldErrorMessage: "" }));
    },
    [validators, ...validators, isValid, errorMessage, dispatch, formId, formSectionId, fieldId]
  );

  useEffect(() => {
    if (initialState) {
      const payload = {
        formId,
        formSectionId,
        fieldId,
        fieldIsValid: initialState,
        fieldErrorMessage: "",
      };
      dispatch(SetFieldIsValid(payload));
    } else {
      validate(initialValue);
    }
  }, [initialState, formId, formSectionId, fieldId, dispatch, initialValue]);

  return validate;
};

export default useValidator;
